import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import { keys } from '../../api/keys'
import { emit } from '../../platform/analytics/analytics'
import type { ENUM_VALUES } from '../../platform/analytics/vocabulary'
import { useToast } from '../../platform/feedback/ToastProvider'
import { memorySuggestionOf, toTxn, type MemorySuggestion, type Txn } from './transactionWire'
import { getTransaction } from './useTransaction'

export type FieldKind = (typeof ENUM_VALUES.field_kind)[number]
export type SaveStatus = 'idle' | 'saving' | 'saved' | 'failed'
export type TransactionUpdate = components['schemas']['TransactionUpdate']

/** How long "Saved" stays up. */
export const SAVED_MS = 2000

type Options<V> = {
  /** Wait this long after the last `set` before sending; `flush()` sends at once. Absent: send immediately. */
  debounceMs?: number
  /** The value as words, for "It's back to …". Absent for free text: a note's words are never echoed. */
  describe?: (v: V) => string
  /**
   * After the SERVER confirms the latest edit: the row it answered with, and the value this save
   * replaced, i.e. what the detail cache held when the save started. When an earlier save of the
   * same field was queued ahead of it, that is the earlier save's answer, not the original.
   */
  onConfirmed?: (txn: Txn, before: V) => void
  /**
   * On EVERY answer the server gives, in the order it applied them, including one for an edit that
   * has since been superseded. For a screen that cannot observe the detail cache this is how it keeps
   * the row the server holds: after an intermediate success then a failure, that row is the truth.
   */
  onAnswer?: (txn: Txn) => void
  /** The server answered the LATEST edit with a learned-memory offer (`memory_suggestion`, non-null). Category only in practice. */
  onMemorySuggestion?: (s: MemorySuggestion) => void
  onStatus?: (kind: FieldKind, status: SaveStatus) => void
  /**
   * For a field the server can change on its own (a rule adds a tag): at SEND time, turn the user's edit
   * into their DIFF (`edit` against `from`, the value they edited from) applied to `latest`, what the
   * server holds now. Without it the edit is sent as made, which would overwrite whatever the server
   * added while an earlier save was running.
   */
  rebase?: (edit: V, from: V, latest: V) => V
}

/**
 * One autosaving field of the transaction detail.
 *
 * `value` is the user's latest edit while it is being saved, then **whatever the
 * PATCH answered**: the server re-runs rules on every update, so the response
 * can differ from the request and it is the truth. It is written into the
 * detail cache, and `server` (read from that cache) takes over.
 *
 * **Only the latest edit's outcome counts.** Each `set` takes the next sequence
 * number; an answer, good or bad, from any older one is dropped. Two quick edits
 * can resolve in reverse, and applying the older would leave the field on a
 * value the user has already moved away from. A failure puts back `server`: the
 * last value the server confirmed, never an intermediate optimistic one. The
 * detail is refetched too, because an older edit that was dropped may still
 * have succeeded on the server.
 *
 * **One PATCH in flight per transaction.** The sequence number orders what the
 * UI shows, not what the server receives: two requests sent together land in
 * arrival order, and a whole-row answer written late rolls back a different
 * field's confirmed value. So every save goes through a mutation in the scope
 * `txn-<id>`, which TanStack Query runs one at a time across all five fields
 * (a failed one releases the next). Within one field the edits made while a
 * save is running, or while the re-read after a failed one is running, collapse into the LATEST value,
 * sent when that save and any re-read after it have finished. The
 * answers therefore arrive in the order the server applied them, and each
 * whole-row answer is a superset of the one before it.
 */
export function useAutosaveField<V>(
  txnId: string,
  kind: FieldKind,
  server: V,
  toPatch: (v: V) => TransactionUpdate,
  /** Reads this field out of a transaction: how the detail cache is asked "what does the server hold now?". */
  read: (txn: Txn) => V,
  opts: Options<V> = {},
): { value: V; set: (v: V) => void; flush: () => void; status: SaveStatus } {
  const client = useQueryClient()
  const toast = useToast()
  const [draft, setDraft] = useState<{ v: V } | null>(null)
  const [status, setStatus] = useState<SaveStatus>('idle')
  const value = draft !== null ? draft.v : server
  const latest = useRef(0)
  const queued = useRef<{ v: V; seq: number; from: { v: V } } | null>(null)
  const busy = useRef(false)
  /**
   * What the field shows as of the last commit: the draft while one is held, otherwise `server`. Written
   * by a layout effect, so it is what the user is looking at when they click. NOT pinned by any test:
   * jsdom cannot reach the frame between a cache write and a passive effect, so `useLayoutEffect` over
   * `useEffect` rests on this reasoning alone.
   */
  const onScreen = useRef<{ v: V }>({ v: server })
  /**
   * An edit not yet sent carries its own `from`: the value the user saw when they began the oldest unsent
   * edit. It travels with the edit, and after a FAILED save the next one inherits the failed save's base,
   * because a failure confirmed nothing and the user's own unconfirmed edit is not a server change.
   */
  const waiting = useRef<{ v: V; seq: number; from: { v: V } } | null>(null)
  // `networkMode: 'always'`: offline the PATCH is attempted and rejects at once. The default would PAUSE it
  // until a connection returned, and every later save and re-read in this row's scope would queue behind it.
  const { mutateAsync } = useMutation({
    scope: { id: `txn-${txnId}` },
    networkMode: 'always',
    // The body is built when the mutation RUNS, not when it is queued: the scope holds it behind other
    // saves of this row, and a rebased write needs what those saves answered.
    mutationFn: async (build: () => TransactionUpdate) => {
      let got: PatchAnswer
      try {
        got = await requestPatchAnswer(txnId, build())
      } catch (thrown) {
        // A failure the client saw may still have committed (lost response, 5xx). PATCH is a partial update,
        // so another field's body is its own; but a body derived from the cache (tags, via `rebase`) would be
        // built on a row the server has since moved past. So the re-read happens HERE, before this mutation
        // settles and the scope releases the next one, whatever field it belongs to. A 4xx refusal did not
        // commit and needs none. A thrown fetch is not a SaveError: it is "no response at all".
        const e = thrown instanceof SaveError ? thrown : new SaveError(0)
        if (mayHaveCommitted(e.kind)) {
          try {
            const truth = await getTransaction(txnId)
            client.setQueryData(keys.transactions.detail(txnId), truth)
            e.truth = truth
          } catch {
            void client.invalidateQueries({ queryKey: keys.transactions.detail(txnId) })
            e.readFailed = true
          }
        }
        throw e
      }
      // Written here, not after the caller resumes, so the next queued save reads this answer. Answers arrive
      // in the order the server applied them, so each one is the newest truth.
      client.setQueryData(keys.transactions.detail(txnId), got.txn)
      return got
    },
  })
  // The re-read after a failure runs in the SAME scope, so it serialises with every save of this row:
  // no PATCH can start while it is in flight, and its answer cannot be written over a newer one.
  // Same `networkMode: 'always'`, and no retry: offline it rejects at once instead of waiting for a connection.
  const { mutateAsync: readTruth } = useMutation({
    scope: { id: `txn-${txnId}` },
    networkMode: 'always',
    retry: false,
    mutationFn: () => getTransaction(txnId),
  })
  const debounce = useRef<number | undefined>(undefined)
  const savedTimer = useRef<number | undefined>(undefined)
  const current = useRef({ server, toPatch, read, opts })
  useEffect(() => {
    current.current = { server, toPatch, read, opts }
  })

  const { onStatus } = opts
  useEffect(() => {
    onStatus?.(kind, status)
  }, [onStatus, kind, status])

  const send = useCallback(
    async (v: V, seq: number, from: { v: V }): Promise<void> => {
      if (busy.current) {
        waiting.current = { v, seq, from: waiting.current?.from ?? from }
        return
      }
      busy.current = true
      const { toPatch: patch, opts: o } = current.current
      // Read the cache, not the `server` ref: a queued save starts before the render that would refresh the ref.
      const heldNow = (): V => {
        const cached = client.getQueryData<Txn>(keys.transactions.detail(txnId))
        return cached === undefined ? current.current.server : current.current.read(cached)
      }
      const before = heldNow()
      let answer: Txn | undefined
      let suggestion: MemorySuggestion | null = null
      let failure: SaveFailure = 'network'
      let failed: SaveError | undefined
      try {
        const got = await mutateAsync(() => {
          const rebase = current.current.opts.rebase
          return patch(rebase !== undefined ? rebase(v, from.v, heldNow()) : v)
        })
        answer = got.txn
        suggestion = got.memorySuggestion
      } catch (e) {
        answer = undefined
        if (e instanceof SaveError) {
          failure = e.kind
          failed = e
        }
      }
      // `busy` stays held through the re-read below: an edit made during it queues, and is then given the
      // failed save's base. Released in `finally`, so nothing in this block can strand the queue.
      try {
        if (answer !== undefined) {
          // Every save the server accepted counts, including one a later edit superseded. The kind only: never the value.
          emit('transaction_edited', { field_kind: kind })
          o.onAnswer?.(answer)
          void client.invalidateQueries({ queryKey: keys.transactions.list() })
        }
        if (answer === undefined) emit('transaction_edit_failed', { field_kind: kind, error_kind: failure })
        if (seq === latest.current) {
          if (answer !== undefined) {
            setDraft(null)
            setStatus('saved')
            window.clearTimeout(savedTimer.current)
            savedTimer.current = window.setTimeout(() => setStatus('idle'), SAVED_MS)
            o.onConfirmed?.(answer, before)
            if (suggestion !== null) o.onMemorySuggestion?.(suggestion)
          } else {
            // Hand the server's row back after a rejected save, so the field and Review show what it holds,
            // including a consumer that cannot observe the detail cache. A failure that may have committed
            // (5xx, no response) was already re-read inside the mutation and that answer is used, never a
            // second GET. So a 4xx costs exactly one GET here: pre-existing and intended.
            let truth: Txn | undefined = failed?.truth
            if (truth === undefined && failed?.readFailed !== true) {
              try {
                truth = await readTruth()
                client.setQueryData(keys.transactions.detail(txnId), truth)
              } catch {
                truth = undefined
                void client.invalidateQueries({ queryKey: keys.transactions.detail(txnId) })
              }
            }
            // An edit made while that read was in flight owns the field now; its own answer is newer.
            // Policy for a removal made around here: during the re-read the screen still shows the failed
            // tag, so the removal is a diff on it and the failed tag is sent (e.g. `[one]`). One frame after
            // the re-read the screen shows the settled value, so it is sent without it (e.g. `[]`). That
            // matches the "It's back to …" toast below, and no saved value is lost either way.
            if (seq === latest.current) {
              if (truth !== undefined) o.onAnswer?.(truth)
              setDraft(null)
              setStatus('failed')
              const back = o.describe ? ` It's back to ${o.describe(heldNow())}.` : ''
              toast.show({ kind: 'error', message: `Couldn't save the ${kind}.${back}` })
            }
          }
        }
      } finally {
        busy.current = false
        const next = waiting.current
        waiting.current = null
        if (next !== null) void send(next.v, next.seq, answer !== undefined ? next.from : from)
      }
    },
    [client, kind, mutateAsync, readTruth, toast, txnId],
  )

  const flush = useCallback(() => {
    window.clearTimeout(debounce.current)
    const q = queued.current
    queued.current = null
    if (q !== null) void send(q.v, q.seq, q.from)
  }, [send])

  useLayoutEffect(() => {
    onScreen.current = { v: value }
  })

  /**
   * The value the user is editing FROM, which a rebase diffs against. In order of precedence:
   * the oldest debounced edit not yet sent; the oldest edit queued behind a save; what the field shows
   * right now (`onScreen`). The first two are inherited so a run of edits collapses to one diff, and
   * `send` hands a failed save's base on. The last is never `server`, which only follows the cache after
   * a passive effect and so trails it.
   */
  const baseOfNextEdit = useCallback((): { v: V } => {
    if (queued.current !== null) return queued.current.from
    if (waiting.current !== null) return waiting.current.from
    return onScreen.current
  }, [])

  const set = useCallback(
    (v: V) => {
      const seq = ++latest.current
      const from = baseOfNextEdit()
      setDraft({ v })
      setStatus('saving')
      window.clearTimeout(savedTimer.current)
      const wait = current.current.opts.debounceMs ?? 0
      if (wait > 0) {
        queued.current = { v, seq, from }
        window.clearTimeout(debounce.current)
        debounce.current = window.setTimeout(flush, wait)
      } else {
        void send(v, seq, from)
      }
    },
    [baseOfNextEdit, flush, send],
  )

  // Leaving the screen with a debounced edit still waiting sends it rather than dropping it.
  useEffect(
    () => () => flush(),
    [flush],
  )
  useEffect(() => () => window.clearTimeout(savedTimer.current), [])

  return { value, set, flush, status }
}

export type SaveFailure = (typeof ENUM_VALUES.error_kind)[number]

/**
 * Why a save failed, as analytics words it: by what the STATUS says about who has to act, not by its class.
 *
 * - `not_found`: 404, the row is gone.
 * - `rejected`: 400 and 422, the server refused the value itself. Any other 4xx not named below
 *   (409, 429 included; the transaction PATCH returns neither today) is a refused request too, so it
 *   lands here.
 * - `unauthorised`: 401 and 403, the session or the permission, not the value.
 * - `server`: 5xx and nothing else.
 * - `network`: no response at all (a thrown fetch). A 2xx whose body would not parse also lands here.
 */
export function saveFailureKind(status: number): SaveFailure {
  if (status === 404) return 'not_found'
  if (status === 401 || status === 403) return 'unauthorised'
  if (status >= 500) return 'server'
  if (status >= 400) return 'rejected'
  return 'network'
}

/** A failure after which the server may have applied the change: no response, or a 5xx. A 4xx refused it. */
export function mayHaveCommitted(kind: SaveFailure): boolean {
  return kind === 'network' || kind === 'server'
}

/** What `requestPatch` rejected with, as a failure kind. A thrown fetch is not a SaveError: it is "no response at all". */
export function failureOf(thrown: unknown): SaveFailure {
  return thrown instanceof SaveError ? thrown.kind : 'network'
}

class SaveError extends Error {
  readonly kind: SaveFailure
  /** Set by the mutation when it re-read the row after a failure that may have committed. */
  truth?: Txn
  /** The re-read itself failed: the detail was invalidated, and a second GET now would only fail again. */
  readFailed?: boolean
  constructor(status: number) {
    super('transaction-update-failed')
    this.kind = saveFailureKind(status)
  }
}

/** A PATCH's answer: the row, and the learned-memory offer the server attached (null on every non-category edit). */
export type PatchAnswer = { txn: Txn; memorySuggestion: MemorySuggestion | null }

/**
 * The one PATCH, shared by this hook and the list's quick sets (`useQuickCategory`, `useQuickMerchant`). Resolves to
 * the server's answer; rejects on any non-2xx. Classify the rejection with `failureOf`.
 */
export async function requestPatchAnswer(id: string, body: TransactionUpdate): Promise<PatchAnswer> {
  const { data, error, response } = await api.PATCH('/api/v1/transactions/{transaction_id}', {
    params: { path: { transaction_id: id } },
    body,
  })
  if (error !== undefined || data === undefined) throw new SaveError(response.status)
  return { txn: toTxn(data), memorySuggestion: memorySuggestionOf(data) }
}

/** `requestPatchAnswer` for `useMerchantForRows`, which ignores the memory offer on purpose (a many-row set, like bulk). */
export async function requestPatch(id: string, body: TransactionUpdate): Promise<Txn> {
  return (await requestPatchAnswer(id, body)).txn
}
